/**
 * src/lib/pdfMotTrang.js — dựng file PDF thật cho bản một trang.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * VÌ SAO CẦN PDF THẬT, KHÔNG PHẢI HTML
 *
 * Trước đây nút chia sẻ gửi đi file .html. Trên điện thoại — nơi phần lớn
 * người Việt chia sẻ qua Zalo và Messenger — người nhận bấm vào thường không
 * mở được, hoặc mở ra mất logo. Founder gửi hồ sơ cho nhà đầu tư mà bên kia
 * không xem được là hỏng ở đúng bước quan trọng nhất.
 *
 * PDF thì mở được ở mọi nơi, in ra đúng như trên màn hình, và chữ vẫn chọn
 * và tìm kiếm được (khác với cách chụp màn hình rồi nhét vào PDF, vừa nặng
 * vừa mờ vừa không copy được).
 *
 * VỀ FONT: PDF không có sẵn font nào đủ chữ tiếng Việt — dùng font mặc định
 * thì "ệ", "ỡ", "ằ" biến thành ô vuông hoặc mất dấu. Nên phải nhúng Be Vietnam
 * Pro. Nhúng dạng subset (chỉ những chữ thực sự dùng) nên file chỉ vài chục KB
 * chứ không phải vài trăm.
 *
 * Thư viện và font đều nạp theo kiểu lười: ai không xuất PDF thì không tải.
 * ─────────────────────────────────────────────────────────────────────────
 */

import { noiDungMotTrang } from './motTrang.js';

const A4 = { rong: 595.28, cao: 841.89 };
const LE = 40;
const RONG = A4.rong - LE * 2;

/* Cỡ chữ, tính bằng point. */
const CO = { h1: 19, moTa: 11, phu: 9, than: 9.5, h2: 8.5, h3: 10, nho: 7.8 };

const MUC = { r: 0.06, g: 0.07, b: 0.09 };
const NHAT = { r: 0.38, g: 0.42, b: 0.47 };
const VIEN = { r: 0.89, g: 0.90, b: 0.93 };
const DIEM = { r: 0.11, g: 0.30, b: 0.85 };

/**
 * Cắt chuỗi thành các dòng vừa bề rộng cho trước.
 * Tự cắt cả từ quá dài (đường dẫn, email không dấu cách) để không tràn lề.
 */
export function catDong(chu, font, co, rong) {
  const ra = [];
  for (const doan of String(chu ?? '').split('\n')) {
    let dong = '';
    for (const tu of doan.split(/\s+/).filter(Boolean)) {
      const thu = dong ? `${dong} ${tu}` : tu;
      if (font.widthOfTextAtSize(thu, co) <= rong) {
        dong = thu;
        continue;
      }
      if (dong) ra.push(dong);
      if (font.widthOfTextAtSize(tu, co) <= rong) {
        dong = tu;
        continue;
      }
      // Một "từ" dài hơn cả dòng: cắt theo ký tự.
      let phan = '';
      for (const ky of tu) {
        if (font.widthOfTextAtSize(phan + ky, co) > rong) {
          ra.push(phan);
          phan = ky;
        } else phan += ky;
      }
      dong = phan;
    }
    ra.push(dong);
  }
  return ra.length ? ra : [''];
}

/** Ảnh logo dạng data URL -> byte, kèm loại để pdf-lib nhúng đúng cách. */
function doiLogo(dataUrl) {
  const m = /^data:image\/(png|jpeg);base64,([A-Za-z0-9+/=]+)$/.exec(dataUrl || '');
  if (!m) return null; // WEBP và SVG: pdf-lib không nhúng được, bỏ qua chứ không lỗi
  const nhiPhan = Uint8Array.from(atob(m[2]), (c) => c.charCodeAt(0));
  return { loai: m[1], byte: nhiPhan };
}

/**
 * @param {object} data Bản chuẩn bị
 * @param {'nha-dau-tu'|'noi-bo'} cheDo
 * @returns {Promise<Uint8Array>} byte của file PDF
 */
export async function dungPdfMotTrang(data, cheDo = 'nha-dau-tu') {
  const n = noiDungMotTrang(data, cheDo);

  const [{ PDFDocument, rgb }, fontkitMod] = await Promise.all([
    import('pdf-lib'),
    import('@pdf-lib/fontkit'),
  ]);
  const [thuong, dam] = await Promise.all([
    fetch('/fonts/BeVietnamPro-Regular.ttf').then((r) => r.arrayBuffer()),
    fetch('/fonts/BeVietnamPro-SemiBold.ttf').then((r) => r.arrayBuffer()),
  ]);

  const tep = await PDFDocument.create();
  tep.registerFontkit(fontkitMod.default || fontkitMod);
  const fThuong = await tep.embedFont(thuong, { subset: true });
  const fDam = await tep.embedFont(dam, { subset: true });

  tep.setTitle(`${n.ten}: bản một trang`);
  tep.setSubject('Bản tự khai của doanh nghiệp, lập bằng fundraising.vn');
  tep.setProducer('fundraising.vn');

  let trang = tep.addPage([A4.rong, A4.cao]);
  let y = A4.cao - LE;
  const mau = (c) => rgb(c.r, c.g, c.b);

  /** Xuống trang khi không đủ chỗ. Luôn gọi trước khi vẽ một khối. */
  const chuaCho = (can) => {
    if (y - can >= LE) return;
    trang = tep.addPage([A4.rong, A4.cao]);
    y = A4.cao - LE;
  };

  const viet = (chu, { font = fThuong, co = CO.than, c = MUC, x = LE, rong = RONG, cach = 1.45 } = {}) => {
    const dong = catDong(chu, font, co, rong);
    for (const d of dong) {
      chuaCho(co * cach);
      y -= co * cach;
      trang.drawText(d, { x, y, size: co, font, color: mau(c) });
    }
  };

  const ke = (dem = 6) => {
    chuaCho(dem + 2);
    y -= dem;
    trang.drawLine({
      start: { x: LE, y }, end: { x: LE + RONG, y },
      thickness: 0.7, color: mau(VIEN),
    });
  };

  /* ---------------- Đầu trang ---------------- */

  const anh = doiLogo(n.logo);
  let xChu = LE;
  let rongChu = RONG;
  if (anh) {
    try {
      const img = anh.loai === 'png' ? await tep.embedPng(anh.byte) : await tep.embedJpg(anh.byte);
      const caoLogo = 46;
      const tyLe = Math.min(caoLogo / img.height, 110 / img.width);
      const w = img.width * tyLe;
      const h = img.height * tyLe;
      trang.drawImage(img, { x: LE, y: y - h, width: w, height: h });
      xChu = LE + w + 14;
      rongChu = RONG - w - 14;
    } catch {
      // Logo hỏng thì bỏ qua: thiếu logo vẫn còn tài liệu, lỗi thì mất tất cả.
    }
  }

  const yDau = y;
  for (const d of catDong(n.ten, fDam, CO.h1, rongChu)) {
    y -= CO.h1 * 1.2;
    trang.drawText(d, { x: xChu, y, size: CO.h1, font: fDam, color: mau(MUC) });
  }
  if (n.moTa) viet(n.moTa, { co: CO.moTa, x: xChu, rong: rongChu, cach: 1.35 });
  if (n.phuDe) viet(n.phuDe, { co: CO.phu, c: NHAT, x: xChu, rong: rongChu });
  if (anh) y = Math.min(y, yDau - 52);

  /* ---------------- Dòng tự khai ---------------- */

  y -= 14;
  const dongTuKhai = catDong(n.tuKhai, fThuong, CO.nho, RONG - 16);
  const caoHop = dongTuKhai.length * CO.nho * 1.45 + 10;
  chuaCho(caoHop);
  trang.drawRectangle({
    x: LE, y: y - caoHop, width: RONG, height: caoHop, color: rgb(0.957, 0.965, 0.988),
  });
  trang.drawRectangle({ x: LE, y: y - caoHop, width: 2.5, height: caoHop, color: mau(DIEM) });
  let yt = y - 5;
  for (const d of dongTuKhai) {
    yt -= CO.nho * 1.45;
    trang.drawText(d, { x: LE + 10, y: yt, size: CO.nho, font: fThuong, color: mau(NHAT) });
  }
  y -= caoHop;

  /* ---------------- Số vốn đang gọi ---------------- */

  if (n.goiVon) {
    y -= 16;
    chuaCho(40);
    trang.drawRectangle({
      x: LE, y: y - 34, width: RONG, height: 34,
      borderColor: mau(VIEN), borderWidth: 0.8,
    });
    trang.drawText('ĐANG GỌI', { x: LE + 14, y: y - 21, size: CO.phu, font: fThuong, color: mau(NHAT) });
    const xSo = LE + 14 + fThuong.widthOfTextAtSize('ĐANG GỌI', CO.phu) + 10;
    trang.drawText(n.goiVon.soTien, { x: xSo, y: y - 23, size: 14, font: fDam, color: mau(MUC) });
    if (n.goiVon.vong) {
      const xV = xSo + fDam.widthOfTextAtSize(n.goiVon.soTien, 14) + 10;
      trang.drawText(`vòng ${n.goiVon.vong}`, {
        x: xV, y: y - 21, size: CO.phu, font: fThuong, color: mau(NHAT),
      });
    }
    y -= 34;
  }

  /* ---------------- Các mục ---------------- */

  const veBang = (hang) => {
    const cotTrai = RONG * 0.44;
    for (const [k, v] of hang) {
      const dt = catDong(k, fThuong, CO.than, cotTrai - 10);
      const dp = catDong(v, fThuong, CO.than, RONG - cotTrai);
      const cao = Math.max(dt.length, dp.length) * CO.than * 1.45;
      chuaCho(cao + 4);
      const yBatDau = y;
      dt.forEach((d, i) => trang.drawText(d, {
        x: LE, y: yBatDau - (i + 1) * CO.than * 1.45, size: CO.than, font: fThuong, color: mau(NHAT),
      }));
      dp.forEach((d, i) => trang.drawText(d, {
        x: LE + cotTrai, y: yBatDau - (i + 1) * CO.than * 1.45, size: CO.than, font: fThuong, color: mau(MUC),
      }));
      y -= cao + 3;
    }
  };

  for (const m of n.muc) {
    y -= 16;
    chuaCho(30);
    y -= CO.h2 * 1.3;
    trang.drawText(m.h.toUpperCase(), {
      x: LE, y, size: CO.h2, font: fDam, color: mau(NHAT),
    });
    ke(5);
    y -= 5;

    if (m.kieu === 'bang') veBang(m.hang);
    else if (m.kieu === 'danh-sach') {
      for (const i of m.items) viet(`•  ${i}`);
    } else if (m.kieu === 'hon-hop') {
      if (m.doan) viet(m.doan);
      if (m.hang.length) { y -= 4; veBang(m.hang); }
    } else if (m.kieu === 'ketqua') {
      for (const k of m.khoi) {
        y -= 9;
        chuaCho(24);
        y -= CO.h3 * 1.3;
        trang.drawText(k.ten, { x: LE, y, size: CO.h3, font: fDam, color: mau(MUC) });
        y -= 3;
        if (k.nguon) viet(k.nguon.chu, { co: CO.nho, c: NHAT });
        if (k.hang.length) veBang(k.hang);
        for (const g of k.ghiChu) viet(`•  ${g}`, { c: NHAT });
        if (!n.guiRaNgoai && k.coDiem) {
          viet(
            'Điểm do doanh nghiệp tự chấm theo bảng tiêu chí công khai tại fundraising.vn, '
            + 'dựa trên dữ liệu tự khai. Không phải đánh giá của bên thứ ba.',
            { co: CO.nho, c: NHAT },
          );
        }
      }
    }
  }

  if (n.trong) {
    y -= 14;
    viet('Bản chuẩn bị chưa có dữ liệu. Hãy điền hồ sơ và chạy vài công cụ rồi xuất lại.', { c: NHAT });
  }

  /* ---------------- Chân trang ---------------- */

  y -= 20;
  ke(4);
  y -= 4;
  for (const c of n.chanTrang) viet(c, { co: CO.nho, c: NHAT, cach: 1.5 });

  // Đánh số trang: tài liệu nhiều trang mà không có số thì người nhận in ra dễ
  // xáo thứ tự, và không biết mình có thiếu trang nào không.
  const cacTrang = tep.getPages();
  if (cacTrang.length > 1) {
    cacTrang.forEach((t, i) => {
      const nhan = `${i + 1}/${cacTrang.length}`;
      t.drawText(nhan, {
        x: A4.rong - LE - fThuong.widthOfTextAtSize(nhan, CO.nho),
        y: LE - 16, size: CO.nho, font: fThuong, color: mau(NHAT),
      });
    });
  }

  return tep.save();
}
